import { generate } from '@pdfme/generator'
import { text, line } from '@pdfme/schemas'
import type { Template } from '@pdfme/common'
import type { Expense, Vendor, Property, AccountSettings } from '@/types'
import { getThaiFontConfig } from '@/lib/thaiFont'
import { buildInputsFromTemplate, ensureFontName } from '@/lib/pdfHelpers'

// Note: locale is undefined intentionally — pdfme canvas rendering with Thai fonts has issues
// with Thai-script digits; ASCII commas are required for correct layout. Do NOT migrate this
// formatCurrency() to formatBaht() from web/src/lib/money.ts.
function formatCurrency(amount: number): string {
  return amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('th-TH', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export function buildWhtVariableMap(
  expense: Expense,
  vendor: Vendor | null,
  property: Property,
  accountSettings?: AccountSettings
): Record<string, string> {
  const payerName = property.name || accountSettings?.name || 'FlowRent'

  // Payer block — the property issuing the WHT certificate
  const payerLines: string[] = [payerName]
  if (property.address) payerLines.push(property.address)
  if (property.tax_id) payerLines.push(`เลขประจำตัวผู้เสียภาษี: ${property.tax_id}`)

  // Payee block — the vendor receiving the payment
  const payeeName = vendor?.name || expense.vendor_name_resolved || expense.vendor_name || '-'
  const payeeLines: string[] = [payeeName]
  if (vendor?.address) payeeLines.push(vendor.address)
  if (vendor?.tax_id) payeeLines.push(`เลขประจำตัวผู้เสียภาษี: ${vendor.tax_id}`)

  const whtRate = expense.wht_rate ?? 0
  const whtRatePercent = (whtRate * 100).toFixed(1) + '%'

  const description = expense.description || expense.category || '-'

  return {
    cert_number: `WHT-${expense.id}`,
    payer_info: payerLines.join('\n'),
    payee_info: payeeLines.join('\n'),
    expense_description: description,
    expense_date: formatDate(expense.expense_date),
    gross_amount: formatCurrency(Number(expense.total_amount)),
    wht_rate: whtRatePercent,
    wht_amount: formatCurrency(Number(expense.wht_amount)),
    net_amount: formatCurrency(Number(expense.net_cash_amount)),
    footer: 'Generated by FlowRent',
  }
}

function buildPlugins() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const plugins: Record<string, any> = { text, line }
  return plugins
}

export async function generateWhtCertificatePdf(
  expense: Expense,
  vendor: Vendor | null,
  property: Property,
  template: Template,
  accountSettings?: AccountSettings
): Promise<Uint8Array> {
  const font = await getThaiFontConfig()
  const vars = buildWhtVariableMap(expense, vendor, property, accountSettings)
  const tmpl = ensureFontName(template)
  const inputs = buildInputsFromTemplate(tmpl, vars)
  const plugins = buildPlugins()

  return await generate({ template: tmpl, inputs, plugins, options: { font } })
}

export async function downloadWhtCertificatePdf(
  expense: Expense,
  vendor: Vendor | null,
  property: Property,
  template: Template,
  accountSettings?: AccountSettings
): Promise<void> {
  const pdfData = await generateWhtCertificatePdf(expense, vendor, property, template, accountSettings)
  const blob = new Blob([pdfData.buffer as ArrayBuffer], { type: 'application/pdf' })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `wht-certificate-${expense.id}.pdf`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}
